import React, {
  CSSProperties,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';

import { ArrowDown } from '../Icons';

import styles from './responsiveTable.module.scss';
import { useStickyTableHeader } from './useStickyTableHeader';

import { TABLE_STYLE, TABLE_TYPE } from 'types/common.interface';
import { Checkbox, Radio } from 'views/components/UI/Form-elements';

const CELL_HORIZONTAL_PADDING = 32;
const TABLE_WIDTH_BUFFER = 24;
// Includes space around the three-dot control so data never crowds the actions.
const ACTION_COLUMN_WIDTH = 72;
// Leading checkbox/selection column.
const SELECT_COLUMN_WIDTH = 40;
let nextTableId = 0;

type TableSortDirection = 'asc' | 'desc';

interface IResponsiveTableRow {
  id: number | string;
}

interface IResponsiveTableColumn<T extends IResponsiveTableRow> {
  key: Extract<keyof T, string>;
  label: ReactNode;
  priority: number;
  minWidth: number;
  sortable?: boolean;
  // Receives the desired direction so a column can enforce custom ordering
  // (e.g. keeping unset values last regardless of direction).
  sortComparator?: (a: T, b: T, direction: TableSortDirection) => number;
  // When a row has pop-in content, indent it to start under this column
  // instead of the row's first column. Meaningful on at most one column.
  popInAnchor?: boolean;
  // The column holds quiet (tertiary) buttons: pull them back by their own
  // padding so the label aligns with the column heading. Applies to the table
  // cell only, not to the stacked pop-in layout.
  flushControls?: boolean;
}

interface ITableSortState {
  key: string;
  direction: TableSortDirection;
}

interface IResponsiveTableRowSelection {
  type: TABLE_TYPE;
  disabled?: boolean;
  selectedRowsKeys: number[];
  onChange: (rowIds: number[]) => void;
  clearSelection?: () => void;
}

interface IResponsiveTableProps<T extends IResponsiveTableRow> {
  columns: IResponsiveTableColumn<T>[];
  data: T[];
  /** Initial order only; subsequent header clicks own the sort state. */
  defaultSort?: { key: Extract<keyof T, string>; direction: TableSortDirection };
  renderActions?: (row: T) => ReactNode;
  /** Space reserved for all row actions, including responsive layouts. */
  actionColumnWidth?: number;
  renderCell?: (column: IResponsiveTableColumn<T>, row: T) => ReactNode;
  rowSelection?: IResponsiveTableRowSelection;
  bodyMaxHeight?: string;
  hideHeader?: boolean;
  stickyHeader?: boolean;
  // Viewport offset for page scrolling; height-limited tables stick at their
  // own scroll container's top instead.
  stickyHeaderOffset?: string;
  // Blank space above a viewport-pinned header, matching its containing card.
  stickyHeaderInset?: string;
}

interface IColumnVisibility<T extends IResponsiveTableRow> {
  visibleColumns: IResponsiveTableColumn<T>[];
  collapsedColumns: IResponsiveTableColumn<T>[];
  // Combined minWidth + padding of the columns left visible after collapsing.
  // Columns that must stay visible (priority 1) may still exceed the
  // container's width; the table uses this as its own min-width so those
  // columns render at their declared size and the wrapper scrolls
  // horizontally instead of squeezing them into unreadable ellipsis.
  visibleColumnsWidth: number;
}

const getColumnVisibility = <T extends IResponsiveTableRow>(
  columns: IResponsiveTableColumn<T>[],
  containerWidth: number,
): IColumnVisibility<T> => {
  const visibleColumns = [...columns];
  const collapsedColumns: IResponsiveTableColumn<T>[] = [];
  const availableWidth = Math.max(0, containerWidth - TABLE_WIDTH_BUFFER);
  const collapseCandidates = [...columns]
    .map((column, index) => ({ column, index }))
    .filter(({ column }) => column.priority > 1)
    .sort(
      (first, second) =>
        second.column.priority - first.column.priority || second.index - first.index,
    );

  const getRequiredWidth = (): number =>
    visibleColumns.reduce(
      (width, column) => width + column.minWidth + CELL_HORIZONTAL_PADDING,
      0,
    );

  collapseCandidates.forEach(({ column }) => {
    if (getRequiredWidth() <= availableWidth) {
      return;
    }

    const visibleIndex = visibleColumns.findIndex(
      visibleColumn => visibleColumn.key === column.key,
    );

    if (visibleIndex !== -1) {
      visibleColumns.splice(visibleIndex, 1);
      collapsedColumns.push(column);
    }
  });

  return {
    visibleColumns,
    collapsedColumns: columns.filter(column =>
      collapsedColumns.some(collapsedColumn => collapsedColumn.key === column.key),
    ),
    visibleColumnsWidth: getRequiredWidth(),
  };
};

const defaultSortComparator = <T extends IResponsiveTableRow>(
  column: IResponsiveTableColumn<T>,
  a: T,
  b: T,
  direction: TableSortDirection,
): number => {
  const valueA = a[column.key] as unknown;
  const valueB = b[column.key] as unknown;
  const multiplier = direction === 'desc' ? -1 : 1;

  if (valueA === valueB) {
    return 0;
  }
  if (valueA === null || valueA === undefined || valueA === '') {
    return 1;
  }
  if (valueB === null || valueB === undefined || valueB === '') {
    return -1;
  }
  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return (valueA - valueB) * multiplier;
  }

  return String(valueA).localeCompare(String(valueB)) * multiplier;
};

const getAriaSort = (
  isActiveSort: boolean,
  direction: TableSortDirection,
): 'ascending' | 'descending' | undefined => {
  if (!isActiveSort) {
    return undefined;
  }
  return direction === 'asc' ? 'ascending' : 'descending';
};

const getCellValue = <T extends IResponsiveTableRow>(
  column: IResponsiveTableColumn<T>,
  row: T,
  renderCell?: IResponsiveTableProps<T>['renderCell'],
): ReactNode => {
  if (renderCell) {
    return renderCell(column, row);
  }

  const value = row[column.key] as unknown;
  return value === null || value === undefined || value === '' ? '-' : String(value);
};

const ResponsiveTable = <T extends IResponsiveTableRow>(
  props: IResponsiveTableProps<T>,
): ReactElement => {
  const { columns, data, renderCell, defaultSort } = props;
  const {
    renderActions,
    rowSelection,
    bodyMaxHeight,
    hideHeader,
    actionColumnWidth = ACTION_COLUMN_WIDTH,
  } = props;
  const {
    stickyHeader = Boolean(bodyMaxHeight),
    stickyHeaderOffset = '0px',
    stickyHeaderInset = '0px',
  } = props;
  const [tableId] = useState(() => {
    nextTableId += 1;
    return `responsive-table-${nextTableId}`;
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const viewportSticky = stickyHeader && !hideHeader && !bodyMaxHeight;
  const containerSticky = stickyHeader && !hideHeader && Boolean(bodyMaxHeight);
  const stickyTop =
    stickyHeaderInset === '0px'
      ? stickyHeaderOffset
      : `calc(${stickyHeaderOffset} + ${stickyHeaderInset})`;
  useStickyTableHeader(
    wrapperRef,
    tableRef,
    viewportSticky || containerSticky,
    stickyTop,
    containerSticky,
  );
  const resizeFrameRef = useRef<number | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [sortState, setSortState] = useState<ITableSortState | null>(
    () => defaultSort ?? null,
  );

  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return undefined;
    }

    const updateWidth = (width: number): void => {
      if (resizeFrameRef.current !== null) {
        cancelAnimationFrame(resizeFrameRef.current);
      }

      resizeFrameRef.current = requestAnimationFrame(() => {
        setContainerWidth(currentWidth =>
          currentWidth === width ? currentWidth : width,
        );
      });
    };

    updateWidth(wrapper.clientWidth);

    const observer = new ResizeObserver(entries => {
      const width = Math.floor(entries[0].contentRect.width);
      updateWidth(width);
    });
    observer.observe(wrapper);

    return () => {
      observer.disconnect();

      if (resizeFrameRef.current !== null) {
        cancelAnimationFrame(resizeFrameRef.current);
      }
    };
  }, []);

  const reservedWidth =
    (renderActions ? actionColumnWidth : 0) + (rowSelection ? SELECT_COLUMN_WIDTH : 0);

  const { visibleColumns, collapsedColumns, visibleColumnsWidth } = useMemo(
    () => getColumnVisibility(columns, containerWidth - reservedWidth),
    [columns, containerWidth, reservedWidth],
  );

  const tableMinWidth = visibleColumnsWidth + reservedWidth;

  const sortRows = useCallback(
    (rows: T[]): T[] => {
      if (!sortState) {
        return rows;
      }

      const sortColumn = columns.find(column => column.key === sortState.key);

      if (!sortColumn) {
        return rows;
      }

      return [...rows].sort((a, b) =>
        sortColumn.sortComparator
          ? sortColumn.sortComparator(a, b, sortState.direction)
          : defaultSortComparator(sortColumn, a, b, sortState.direction),
      );
    },
    [columns, sortState],
  );

  const sortedData = useMemo(() => sortRows(data), [data, sortRows]);

  const toggleSort = (column: IResponsiveTableColumn<T>): void => {
    if (!column.sortable) {
      return;
    }

    setSortState(current => {
      if (current?.key !== column.key) {
        return { key: column.key, direction: 'asc' };
      }
      return { key: column.key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const selectedKeys = rowSelection?.selectedRowsKeys ?? [];
  const selectedKeysSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const selectableIds = useMemo(() => data.map(row => Number(row.id)), [data]);
  const selectedCount = selectableIds.filter(id => selectedKeysSet.has(id)).length;
  const areAllSelected = data.length > 0 && selectedCount === selectableIds.length;
  const someSelected = selectedCount > 0 && !areAllSelected;

  const toggleRow = (rowId: number): void => {
    if (!rowSelection || rowSelection.disabled) {
      return;
    }

    const nextKeys = selectedKeysSet.has(rowId)
      ? selectedKeys.filter(key => key !== rowId)
      : [...selectedKeys, rowId];
    rowSelection.onChange(nextKeys);
  };

  const toggleAllRows = (): void => {
    if (!rowSelection || rowSelection.disabled) {
      return;
    }

    if (areAllSelected) {
      rowSelection.onChange(selectedKeys.filter(key => !selectableIds.includes(key)));
    } else {
      rowSelection.onChange([
        ...selectedKeys,
        ...selectableIds.filter(id => !selectedKeysSet.has(id)),
      ]);
    }
  };

  const selectRow = (rowId: number): void => {
    if (!rowSelection || rowSelection.disabled) {
      return;
    }

    rowSelection.onChange([rowId]);
  };

  const totalColumnCount =
    (rowSelection ? 1 : 0) + visibleColumns.length + (renderActions ? 1 : 0);

  const popInAnchorIndex = visibleColumns.findIndex(column => column.popInAnchor);
  const popInLeadingColumnCount =
    (rowSelection ? 1 : 0) + (popInAnchorIndex > 0 ? popInAnchorIndex : 0);

  const renderHeaderCells = (): ReactElement => (
    <tr>
      {rowSelection && (
        <th aria-label="Select" className={styles.selectHeader} scope="col">
          {rowSelection.type === TABLE_STYLE.CHECKBOX_PARENT && (
            <Checkbox
              id={`${tableId}-select-all`}
              disabled={rowSelection.disabled}
              aria-label="Select all rows"
              aria-checked={someSelected ? 'mixed' : areAllSelected}
              checked={areAllSelected}
              isSpecial={someSelected}
              onChange={toggleAllRows}
            />
          )}
        </th>
      )}
      {visibleColumns.map(column => {
        const isActiveSort = sortState?.key === column.key;

        return (
          <th
            key={column.key}
            scope="col"
            aria-sort={getAriaSort(isActiveSort, sortState?.direction ?? 'asc')}
          >
            {column.sortable ? (
              <button
                type="button"
                className={styles.sortButton}
                onClick={() => toggleSort(column)}
                aria-label={`Sort by ${
                  typeof column.label === 'string' ? column.label : column.key
                }`}
              >
                <span
                  className={classNames({
                    [styles.sortLabelActive]: isActiveSort,
                  })}
                >
                  {column.label}
                </span>
                <span
                  className={classNames(styles.sortIcon, {
                    [styles.sortIconActive]: isActiveSort,
                    [styles.sortIconAsc]: isActiveSort && sortState.direction === 'asc',
                  })}
                >
                  <ArrowDown size="16px" color="currentColor" />
                </span>
              </button>
            ) : (
              column.label
            )}
          </th>
        );
      })}
      {renderActions && (
        <th aria-label="Actions" className={styles.actionHeader} scope="col" />
      )}
    </tr>
  );

  // Each logical row gets its own <tbody> instead of all rows sharing one,
  // even though there's normally just a single row's worth of <tr>s inside.
  // Multiple <tbody> elements render identically to one (browsers don't gap
  // or border between them by default), but it gives the row's main <tr>
  // and its responsive pop-in <tr> a shared element whose bounding box is
  // the row's real, current height — collapsed-columns content included —
  // rather than needing something outside the table to track that.
  const renderBodyRows = (rows: T[]): ReactElement => (
    <>
      {rows.map(row => {
        const rowId = Number(row.id);

        return (
          <tbody key={row.id}>
            <tr className={collapsedColumns.length ? styles.mainRowWithPopIn : undefined}>
              {rowSelection && (
                <td className={styles.selectCell}>
                  {rowSelection.type === TABLE_STYLE.RADIO ? (
                    <Radio
                      id={`${tableId}-row-${row.id}`}
                      name={`${tableId}-selection`}
                      disabled={rowSelection.disabled}
                      aria-label="Select row"
                      checked={selectedKeysSet.has(rowId)}
                      onChange={() => selectRow(rowId)}
                    />
                  ) : (
                    <Checkbox
                      id={`${tableId}-row-${row.id}`}
                      aria-label="Select row"
                      disabled={rowSelection.disabled}
                      checked={selectedKeysSet.has(rowId)}
                      onChange={() => toggleRow(rowId)}
                    />
                  )}
                </td>
              )}
              {visibleColumns.map(column => (
                <td
                  key={column.key}
                  data-flush-controls={column.flushControls || undefined}
                >
                  {getCellValue(column, row, renderCell)}
                </td>
              ))}
              {renderActions && (
                <td className={styles.actionCell}>{renderActions(row)}</td>
              )}
            </tr>
            {collapsedColumns.length > 0 && (
              <tr className={styles.popInRow}>
                {popInLeadingColumnCount > 0 && (
                  <td
                    aria-hidden="true"
                    colSpan={popInLeadingColumnCount}
                    className={styles.popInSpacer}
                  />
                )}
                <td
                  colSpan={totalColumnCount - popInLeadingColumnCount}
                  className={styles.popInCell}
                >
                  {collapsedColumns.map(column => (
                    <div key={column.key} className={styles.popInLine}>
                      <span className={styles.popInLabel}>{column.label}:</span>
                      <span className={styles.popInValue}>
                        {getCellValue(column, row, renderCell)}
                      </span>
                    </div>
                  ))}
                </td>
              </tr>
            )}
          </tbody>
        );
      })}
    </>
  );

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.scrollable]: Boolean(bodyMaxHeight),
        [styles.stickyInContainer]: containerSticky,
        [styles.stickyInViewport]: viewportSticky,
      })}
      ref={wrapperRef}
      style={
        {
          maxHeight: bodyMaxHeight,
          scrollMarginTop: viewportSticky ? stickyTop : undefined,
          '--table-header-inset': viewportSticky ? stickyHeaderInset : '0px',
        } as CSSProperties
      }
    >
      <table
        ref={tableRef}
        className={styles.table}
        style={
          {
            minWidth: `${tableMinWidth}px`,
            '--table-action-width': `${actionColumnWidth}px`,
          } as CSSProperties
        }
      >
        <colgroup>
          {rowSelection && (
            <col
              className={styles.selectColumn}
              style={{ width: `${SELECT_COLUMN_WIDTH}px` }}
            />
          )}
          {visibleColumns.map(column => (
            <col
              key={column.key}
              // With a selection column present, let the data columns flex so the
              // fixed-layout table doesn't inflate the narrow checkbox column.
              style={rowSelection ? undefined : { width: `${column.minWidth}px` }}
            />
          ))}
          {renderActions && (
            <col
              className={styles.actionColumn}
              style={{ width: `${actionColumnWidth}px` }}
            />
          )}
        </colgroup>
        {!hideHeader && <thead>{renderHeaderCells()}</thead>}
        {renderBodyRows(sortedData)}
      </table>
    </div>
  );
};

export type { IResponsiveTableColumn, IResponsiveTableRow, IResponsiveTableRowSelection };
export { getColumnVisibility, ResponsiveTable };
